// src/shared/services/appInsights.ts
// 
// Application Insights with Log Level Support
// ==========================================
// 
// Usage Examples:
// ---------------
// // Initialize (call once in main.tsx)
// initAppInsights();
// setMinLogLevel(LogLevel.Warning); // Only log Warning and above
//
// // Exception tracking with severity
// trackException(error, LogLevel.Critical);
// trackExceptionWithContext(error, { 
//   component: 'LoginForm', 
//   severity: LogLevel.Error,
//   userId: '123' 
// });
//
// // Log level-based tracking
// trackInfo('User logged in successfully');
// trackWarning('API response slow');
// trackError('Failed to save data');
// trackCritical('System failure detected');
//
// // Custom traces with properties
// trackTrace('Custom operation completed', LogLevel.Information, { 
//   operationId: '456', 
//   duration: 1200 
// });

import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';

const reactPlugin = new ReactPlugin();

// Log level configuration
export const LogLevel = {
  Verbose: SeverityLevel.Verbose,     // 0 - Detailed debugging info
  Information: SeverityLevel.Information, // 1 - General information
  Warning: SeverityLevel.Warning,     // 2 - Warnings, potential issues
  Error: SeverityLevel.Error,         // 3 - Handled exceptions, errors
  Critical: SeverityLevel.Critical    // 4 - System crashes, fatal errors
} as const;

export type LogLevelType = typeof LogLevel[keyof typeof LogLevel];

// Configurable minimum log level (only log messages at this level or higher)
let minLogLevel: LogLevelType = LogLevel.Information; // Default to Information and above

export const setMinLogLevel = (level: LogLevelType) => {
  minLogLevel = level;
  console.log(`Application Insights minimum log level set to: ${getLevelName(level)}`);
};

export const getMinLogLevel = () => minLogLevel;

const getLevelName = (level: LogLevelType): string => {
  return Object.keys(LogLevel).find(key => LogLevel[key as keyof typeof LogLevel] === level) || 'Unknown';
};

const shouldLog = (level: LogLevelType): boolean => {
  return level >= minLogLevel;
};

export const appInsights = new ApplicationInsights({
  config: {
    // Use connection string from window.config
    connectionString: window.config?.APPLICATION_INSIGHTS_CONNECTION_STRING,

    enableAutoRouteTracking: true, // track SPA route changes
    extensions: [reactPlugin],
    extensionConfig: {
      [reactPlugin.identifier]: { history: undefined } // we'll set this later when we have router history
    },

    // Disable features not needed for exception tracking only
    disableAjaxTracking: false,
    disableFetchTracking: false,
  }
});

// Add telemetry initializer to include standard properties with every telemetry item
appInsights.addTelemetryInitializer((envelope) => {
  // Add properties to match your API-side logging
  envelope.data = envelope.data || {};
  envelope.data.baseData = envelope.data.baseData || {};
  envelope.data.baseData.properties = envelope.data.baseData.properties || {};

  // Add standard properties that match your backend logging
  const standardProperties = {
    // For Application Insights (matching API side)
    ServiceName: 'MSI.SHELL.APP', // or 'MSI.Shell.UI'
    Environment: window.config?.ENVIRONMENT_NAME || window.config?.APP_ENV || 'unknown',
    
    // For Serilog compatibility (matching API side)  
    Application: 'MSI.SHELL.APP', // or 'MSI.Shell.UI'
    
    // Additional UI-specific properties
    ClientType: 'React-SPA',
    UserAgent: navigator.userAgent,
    Url: window.location.href,
    Timestamp: new Date().toISOString(),
  };

  // Merge with existing properties (don't overwrite if already set)
  Object.entries(standardProperties).forEach(([key, value]) => {
    if (envelope.data?.baseData?.properties && !envelope.data.baseData.properties[key]) {
      envelope.data.baseData.properties[key] = value;
    }
  });

  return true; // Continue processing
});

export { reactPlugin };

export function initAppInsights() {
  try {
    appInsights.loadAppInsights();
    
    // Setup global error handler for unhandled exceptions
    setupGlobalErrorHandler();
    
    console.log('Application Insights initialized with standard properties:', {
      ServiceName: 'MSI.SHELL.APP',
      Environment: window.config?.APP_ENV ||window.config?.ENVIRONMENT_NAME ||  'unknown',
      Application: 'MSI.SHELL.APP'
    });
  } catch (error) {
    console.error('Failed to initialize Application Insights:', error);
  }
}

// Function to update global properties (e.g., when user context changes)
export const updateGlobalProperties = (additionalProperties: Record<string, string>) => {
  appInsights.addTelemetryInitializer((envelope) => {
    if (envelope.data?.baseData?.properties) {
      Object.entries(additionalProperties).forEach(([key, value]) => {
        if (envelope.data?.baseData?.properties) {
          envelope.data.baseData.properties[key] = value;
        }
      });
    }
    return true;
  });
};

// Log level-aware trace tracking
export const trackTrace = (
  message: string, 
  severityLevel: LogLevelType = LogLevel.Information,
  properties?: Record<string, string | number | boolean>
) => {
  if (!shouldLog(severityLevel)) return;
  
  try {
    appInsights.trackTrace(
      { 
        message,
        severityLevel 
      },
      {
        timestamp: new Date().toISOString(),
        logLevel: getLevelName(severityLevel),
        environment: window.config?.APP_ENV || 'unknown',
        ...properties
      }
    );
  } catch (e) {
    console.error('Failed to track trace:', e);
  }
};

// Convenience methods for different log levels
export const trackVerbose = (message: string, properties?: Record<string, string | number | boolean>) => {
  trackTrace(message, LogLevel.Verbose, properties);
};

export const trackInfo = (message: string, properties?: Record<string, string | number | boolean>) => {
  trackTrace(message, LogLevel.Information, properties);
};

export const trackWarning = (message: string, properties?: Record<string, string | number | boolean>) => {
  trackTrace(message, LogLevel.Warning, properties);
};

export const trackError = (message: string, properties?: Record<string, string | number | boolean>) => {
  trackTrace(message, LogLevel.Error, properties);
};

export const trackCritical = (message: string, properties?: Record<string, string | number | boolean>) => {
  trackTrace(message, LogLevel.Critical, properties);
};

// Exception tracking - Primary focus (now with severity levels)
export const trackException = (
  error: Error, 
  severityLevel: LogLevelType = LogLevel.Error,
  properties?: Record<string, string | number | boolean>
) => {
  if (!shouldLog(severityLevel)) return;
  
  try {
    appInsights.trackException({
      exception: error,
      severityLevel,
      properties: {
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        environment: window.config?.APP_ENV || 'unknown',
        logLevel: getLevelName(severityLevel),
        ...properties
      }
    });
  } catch (e) {
    console.error('Failed to track exception:', e);
  }
};

// Enhanced exception tracking with context and severity
export const trackExceptionWithContext = (
  error: Error, 
  context: {
    component?: string;
    action?: string;
    userId?: string;
    severity?: LogLevelType;
    additionalData?: Record<string, unknown>;
  } = {}
) => {
  const properties: Record<string, string | number | boolean> = {
    component: context.component || 'unknown',
    action: context.action || 'unknown',
    timestamp: new Date().toISOString(),
    environment: window.config?.APP_ENV || 'unknown',
  };

  if (context.userId) {
    properties.userId = context.userId;
  }

  if (context.additionalData) {
    // Convert complex objects to strings for Application Insights
    Object.entries(context.additionalData).forEach(([key, value]) => {
      properties[key] = typeof value === 'object' ? JSON.stringify(value) : String(value);
    });
  }

  const severity = context.severity || LogLevel.Error;
  trackException(error, severity, properties);
};

// Setup global error handlers
const setupGlobalErrorHandler = () => {
  // Handle unhandled JavaScript errors
  window.addEventListener('error', (event) => {
    trackExceptionWithContext(
      new Error(event.message), 
      {
        component: 'GlobalErrorHandler',
        action: 'unhandled-error',
        severity: LogLevel.Critical, // Unhandled errors are critical
        additionalData: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          stack: event.error?.stack
        }
      }
    );
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    trackExceptionWithContext(
      error,
      {
        component: 'GlobalErrorHandler',
        action: 'unhandled-promise-rejection',
        severity: LogLevel.Critical // Unhandled promise rejections are critical
      }
    );
  });
};

// Additional tracking functions
export const trackEvent = (
  name: string, 
  properties?: Record<string, string | number | boolean>,
  severityLevel: LogLevelType = LogLevel.Information
) => {
  if (!shouldLog(severityLevel)) return;
  
  try {
    appInsights.trackEvent({ name }, {
      timestamp: new Date().toISOString(),
      logLevel: getLevelName(severityLevel),
      environment: window.config?.APP_ENV || 'unknown',
      ...properties
    });
  } catch (e) {
    console.error('Failed to track event:', e);
  }
};

export const trackPageView = (
  name?: string, 
  properties?: Record<string, string | number | boolean>,
  severityLevel: LogLevelType = LogLevel.Information
) => {
  if (!shouldLog(severityLevel)) return;
  
  try {
    appInsights.trackPageView({
      name,
      properties: {
        timestamp: new Date().toISOString(),
        logLevel: getLevelName(severityLevel),
        environment: window.config?.APP_ENV || 'unknown',
        ...properties
      }
    });
  } catch (e) {
    console.error('Failed to track page view:', e);
  }
};

// User context management
export const setUser = (userId: string, accountId?: string) => {
  try {
    appInsights.setAuthenticatedUserContext(userId, accountId);
    const userProperties: Record<string, string | number | boolean> = { userId };
    if (accountId) {
      userProperties.accountId = accountId;
    }
    trackInfo(`User context set: ${userId}`, userProperties);
  } catch (e) {
    console.error('Failed to set user context:', e);
  }
};

export const clearUser = () => {
  try {
    appInsights.clearAuthenticatedUserContext();
    trackInfo('User context cleared');
  } catch (e) {
    console.error('Failed to clear user context:', e);
  }
};

// Log level utilities for external use
export const getLogLevelName = (level: LogLevelType): string => getLevelName(level);

export const isLogLevelEnabled = (level: LogLevelType): boolean => shouldLog(level);
